import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { CloudArrowUp, FileZip, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function NewProject() {
  const navigate = useNavigate();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef();

  const upload = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please choose a .zip file of your project.");
      return;
    }
    setUploading(true);
    setProgress(0);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/projects", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      navigate(`/projects/${data.id}/flow`);
    } catch (err) {
      toast.error(apiError(err));
      setUploading(false);
    }
  }, [navigate]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    upload(e.dataTransfer.files?.[0]);
  };

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-10 sm:px-10">
        <button onClick={() => navigate("/dashboard")} className="mb-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-button">
          <ArrowLeft size={16} /> Back
        </button>
        <h1 className="font-display text-4xl font-bold tracking-tight">New project</h1>
        <p className="mt-2 text-muted-foreground">Drop your exported project ZIP. We'll take it from there.</p>

        <motion.div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          data-testid="upload-zip-dropzone"
          className={`relative mt-8 cursor-pointer rounded-3xl border-2 border-dashed bg-card p-16 text-center transition-colors duration-200 ${
            dragging ? "border-foreground/60 bg-secondary" : "border-border hover:border-foreground/30"
          }`}>
          {dragging && <div className="tracing-beam absolute inset-x-0 top-0 h-1 rounded-t-3xl" />}
          <input ref={inputRef} type="file" accept=".zip" className="hidden" data-testid="upload-input"
            onChange={(e) => upload(e.target.files?.[0])} />
          <div className={`mx-auto grid h-20 w-20 place-items-center rounded-2xl bg-secondary ${uploading ? "" : "pulse-ring relative"}`}>
            {uploading ? <FileZip size={36} weight="duotone" className="animate-pulse" /> : <CloudArrowUp size={36} weight="duotone" />}
          </div>
          {uploading ? (
            <>
              <p className="mt-6 font-display text-xl font-medium">Uploading your project…</p>
              <div className="mx-auto mt-4 h-2 w-64 overflow-hidden rounded-full bg-secondary">
                <motion.div className="h-full rounded-full bg-primary" animate={{ width: `${progress}%` }} transition={{ ease: "easeOut" }} />
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{progress}%</p>
            </>
          ) : (
            <>
              <p className="mt-6 font-display text-xl font-medium">Drop your project here</p>
              <p className="mt-1 text-sm text-muted-foreground">or click to browse — ZIP files up to 200 MB</p>
              <Button className="mt-6 rounded-full active:scale-95">Choose file</Button>
            </>
          )}
        </motion.div>

        <div className="mt-8 rounded-2xl border border-border bg-card p-6">
          <p className="text-sm font-semibold">What happens next</p>
          <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li>1. We look at your app and detect what it's built with.</li>
            <li>2. We prepare an independent version that runs without Emergent.</li>
            <li>3. We test it, then you click one button to go live.</li>
          </ol>
        </div>
      </div>
    </Layout>
  );
}
